import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'
import { RefreshCw, DollarSign, TrendingUp, ArrowRightLeft } from 'lucide-react'
import { useState } from 'react'

function formatRate(rate: number): string {
  return rate.toFixed(6)
}

export default function FxRates() {
  const queryClient = useQueryClient()
  const [convertAmount, setConvertAmount] = useState<string>('1000')
  const [fromCurrency, setFromCurrency] = useState<string>('EUR')
  const [toCurrency, setToCurrency] = useState<string>('USD')

  const { data: fxRates, isLoading } = useQuery({
    queryKey: ['fx-rates'],
    queryFn: () => api.getFxRates('USD'),
  })

  const refreshMutation = useMutation({
    mutationFn: () => api.refreshFxRates('USD'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fx-rates'] })
    },
  })

  // Create rate map for conversions
  const rateMap: Record<string, number> = { USD: 1 }
  fxRates?.forEach(rate => {
    rateMap[rate.quote_currency] = Number(rate.rate)
  })

  // Calculate conversion
  const convertedAmount = (() => {
    const amount = parseFloat(convertAmount) || 0
    if (fromCurrency === toCurrency) return amount

    // Convert to USD first, then to target
    let usdAmount = amount
    if (fromCurrency !== 'USD') {
      const fromRate = rateMap[fromCurrency]
      if (!fromRate) return null
      usdAmount = amount / fromRate
    }

    if (toCurrency === 'USD') return usdAmount

    const toRate = rateMap[toCurrency]
    if (!toRate) return null
    return usdAmount * toRate
  })()

  const currencies = ['USD', ...(fxRates?.map(r => r.quote_currency) || [])]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">FX Rates</h1>
        <button
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isPending}
          className="btn btn-primary inline-flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
          {refreshMutation.isPending ? 'Refreshing...' : 'Refresh Rates'}
        </button>
      </div>

      {/* Currency Converter */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <ArrowRightLeft className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Currency Converter</h2>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[150px]">
            <label className="block text-sm text-gray-500 mb-1">Amount</label>
            <input
              type="number"
              value={convertAmount}
              onChange={(e) => setConvertAmount(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter amount"
            />
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className="block text-sm text-gray-500 mb-1">From</label>
            <select
              value={fromCurrency}
              onChange={(e) => setFromCurrency(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {currencies.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end pb-2">
            <ArrowRightLeft className="h-5 w-5 text-gray-400" />
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className="block text-sm text-gray-500 mb-1">To</label>
            <select
              value={toCurrency}
              onChange={(e) => setToCurrency(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {currencies.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[150px]">
            <label className="block text-sm text-gray-500 mb-1">Result</label>
            <div className="px-3 py-2 bg-gray-100 rounded-lg font-mono text-lg">
              {convertedAmount !== null
                ? convertedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : 'N/A'
              } {toCurrency}
            </div>
          </div>
        </div>
      </div>

      {/* Rates Table */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Exchange Rates (Base: USD)</h2>
        </div>

        {fxRates && fxRates.length > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
              {fxRates.map(rate => (
                <div key={rate.quote_currency} className="p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-lg font-bold text-gray-900">{rate.quote_currency}</p>
                  <p className="font-mono text-xl text-primary-600">{formatRate(Number(rate.rate))}</p>
                  <p className="text-xs text-gray-500 mt-1">1 USD = {formatRate(Number(rate.rate))} {rate.quote_currency}</p>
                </div>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Currency Pair</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Inverse</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {fxRates.map(rate => (
                    <tr key={rate.quote_currency} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">USD/{rate.quote_currency}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatRate(Number(rate.rate))}</td>
                      <td className="px-4 py-3 text-right font-mono text-gray-500">
                        {formatRate(1 / Number(rate.rate))}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{rate.source || 'Unknown'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(rate.rate_date).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No FX rates available.</p>
            <button
              onClick={() => refreshMutation.mutate()}
              disabled={refreshMutation.isPending}
              className="btn btn-primary"
            >
              Fetch Rates
            </button>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="card bg-blue-50 border-blue-200">
        <h3 className="font-semibold text-blue-900 mb-2">About FX Rates</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>- Rates are automatically refreshed when you upload a new portfolio statement</li>
          <li>- Click "Refresh Rates" to manually update rates from the external API</li>
          <li>- All rates are relative to USD (base currency)</li>
          <li>- Value (USD) columns in portfolio views use these rates for conversion</li>
        </ul>
      </div>
    </div>
  )
}
